import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Bell, Menu, Sun, Moon, Calendar, ChevronDown, User, Settings, LogOut } from 'lucide-react'
import { useTheme } from '@/contexts/theme-context'
import { useNavigate } from 'react-router-dom'

interface StudentHeaderProps {
    toggleSidebar: () => void
    showMenuButton?: boolean
}

import { useAuth } from '@/context/auth-context'

export function StudentHeader({ toggleSidebar, showMenuButton = true }: StudentHeaderProps) {
    const { theme, toggleTheme } = useTheme()
    const navigate = useNavigate()
    const { user, logout } = useAuth()
    const [isProfileOpen, setIsProfileOpen] = useState(false)

    const today = new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
    })

    const quickStats = [
        { label: 'Study', value: '4.5h', subtext: '/6h', color: 'text-blue-500' },
        { label: 'Tasks', value: '3', color: 'text-amber-500' },
        { label: 'Test', value: 'Tomorrow', color: 'text-green-500' },
    ]

    // Helper to get initials
    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2)
    }

    return (
        <header className="flex items-center justify-between gap-4 mb-6 md:mb-8">
            {/* Left: Menu Button (Mobile) + Date */}
            <div className="flex items-center gap-3">
                {showMenuButton && (
                    <button
                        onClick={toggleSidebar}
                        className="lg:hidden p-2.5 rounded-xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm text-theme-secondary shadow-sm border border-slate-200/50 dark:border-slate-700/50"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                )}
                <div>
                    <div className="flex items-center gap-2 text-theme-secondary">
                        <Calendar className="h-4 w-4 hidden sm:block" />
                        <span className="text-xs sm:text-sm font-medium">{today}</span>
                    </div>
                    <h1 className="text-lg sm:text-xl lg:text-2xl font-display font-bold text-theme-primary">
                        Make every hour count, {user?.name.split(' ')[0]}!
                    </h1>
                </div>
            </div>

            {/* Right: Stats + Actions */}
            <div className="flex items-center gap-2 sm:gap-4">
                {/* ... (Stats and Theme Toggle remain same) */}
                <div className="hidden md:flex items-center gap-2">
                    {quickStats.map((stat, i) => (
                        <div
                            key={i}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-full border border-slate-200/50 dark:border-slate-700/50 shadow-sm"
                        >
                            <span className="text-[10px] text-theme-secondary uppercase tracking-wide">{stat.label}</span>
                            <span className={`text-sm font-bold ${stat.color}`}>
                                {stat.value}
                                {stat.subtext && <span className="text-theme-muted font-normal text-xs">{stat.subtext}</span>}
                            </span>
                        </div>
                    ))}
                </div>

                <div className="h-6 w-[1px] bg-slate-200 dark:bg-slate-700 hidden md:block" />

                {/* Theme Toggle */}
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={toggleTheme}
                    className="p-2.5 rounded-xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm text-theme-secondary hover:text-primary-blue transition-colors shadow-sm border border-slate-200/50 dark:border-slate-700/50"
                >
                    {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                </motion.button>

                {/* Notifications */}
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2.5 rounded-xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm text-theme-secondary hover:text-primary-blue transition-colors relative shadow-sm border border-slate-200/50 dark:border-slate-700/50"
                >
                    <Bell className="h-5 w-5" />
                    <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-slate-900"
                    />
                </motion.button>

                {/* User Avatar + Dropdown */}
                <div className="relative">
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setIsProfileOpen(!isProfileOpen)}
                        className="flex items-center gap-2 p-1.5 pr-3 rounded-xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm shadow-sm border border-slate-200/50 dark:border-slate-700/50"
                    >
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-blue to-accent-gold flex items-center justify-center text-white font-bold text-sm">
                            {user ? getInitials(user.name) : 'ST'}
                        </div>
                        <ChevronDown className={`h-4 w-4 text-theme-secondary transition-transform hidden sm:block ${isProfileOpen ? 'rotate-180' : ''}`} />
                    </motion.button>

                    {/* Dropdown Menu */}
                    <AnimatePresence>
                        {isProfileOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setIsProfileOpen(false)} />
                                <motion.div
                                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                    className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50"
                                >
                                    <div className="p-3 border-b border-slate-100 dark:border-slate-800">
                                        <p className="font-semibold text-theme-primary text-sm truncate">{user?.name}</p>
                                        <p className="text-xs text-theme-secondary truncate">{user?.email}</p>
                                    </div>
                                    <div className="p-1">
                                        <button
                                            onClick={() => { setIsProfileOpen(false) }}
                                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-theme-secondary hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                        >
                                            <User className="h-4 w-4" /> Profile
                                        </button>
                                        <button
                                            onClick={() => { setIsProfileOpen(false) }}
                                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-theme-secondary hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                        >
                                            <Settings className="h-4 w-4" /> Settings
                                        </button>
                                        <button
                                            onClick={() => {
                                                setIsProfileOpen(false);
                                                logout();
                                                navigate('/');
                                            }}
                                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg transition-colors"
                                        >
                                            <LogOut className="h-4 w-4" /> Logout
                                        </button>
                                    </div>
                                </motion.div>
                            </>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </header>
    )
}
